/**
 * Today — spec 01, in the one state this build can render truthfully.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS DRAWN, AND WHAT IS HONESTLY MISSING
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Spec 01 §2.3–§2.5 puts an **appointed match card** and a 2 × 2 grid of **four umpire photo
 * buttons** at the centre of this screen. Both are computed from `T6` — the umpire slots on
 * today's matches — and **the system holds no umpire appointments at all**: the appointments
 * store is technical slots only, by construction ("Technical slots are the TD's; umpire
 * slots belong to the Umpires Managers", `10-data-model.md`; the repository's own
 * `appointment_dataset_separation` note), and the demonstration corpus ships none.
 *
 * So this screen renders spec 01 §4's **`Not appointed today`** state, which the spec
 * defines for exactly this case and which ruling Q-01c makes the norm rather than the
 * exception: *"a UM may capture on any match and not only their appointment — restricting
 * it would create silent coverage holes on multi-pitch days"* (gap S11). The day's fixtures
 * are listed as `.apptline` rows and every one of them is a way in to capture.
 *
 * The umpire tiles will replace the roster picker on the capture screen the moment umpire
 * appointments exist. Nothing here fabricates one in the meantime.
 *
 * **The sync pill and the debrief pill are absent, not stubbed** (spec 10 §3, gate G-18):
 * there is no queue yet, and the green chip may never render while a queue is non-empty —
 * a chip drawn before the queue exists could only ever be a claim this build cannot keep.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 79 — THE RELEASE REMINDER IS A CARD, AND ONLY A CARD
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Spec 11 §4.1 asked for a nudge card on Today plus one email at the full time of the day's last
 * match. The Technical Delegate ruled on 13 September 2026 that it is the card alone: ruling R5
 * makes the nightly Daily Report email the only message the system sends on its own, and the
 * disclosure every manager reads says so. Nothing here sends anything, and nothing here asks the
 * server about anybody's unreleased notes — see `releaseReminder`.
 *
 * **Run 79a** bounded it after the QA gate: the card is due from the last match's full time UNTIL
 * the Daily Report builds, and it prints the build time. Both moments come from
 * `GET /um/debrief/schedule`, which runs the nightly build's own arithmetic over the fixtures and
 * the Technical Delegate's two settings, re-read every five minutes (run 79b) — so a changed
 * delay or backstop moves the card with the build.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  fetchDailyReportTimes,
  fetchFixturesForDay,
  fetchFixtureDays,
  fetchMyNotes,
} from "../../api/client.js";
import type { DailyReportTimesBody, FixtureRowView, OwnNoteView } from "../../api/types.js";
import { HowToLink, HowToUse } from "../../app/HowToUse.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync, type AsyncState } from "../../app/useAsync.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
import {
  debriefNudgeLine,
  matchDebriefPath,
  matchHasLikelyEnded,
  tokyoDateKicker,
} from "./matchDebrief.js";

/** Today in Asia/Tokyo — the server stores UTC, the app renders Tokyo (spec 00 §7.3). */
export function tokyoToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/**
 * `Tuesday 29 September` — spec 01 §2.1: British English, long form, no ordinal suffix and
 * no comma before the month.
 *
 * **Moved to `matchDebrief.ts` in run 75** and re-exported here unchanged, so nothing that
 * already named it has to move. It moved because the Technical Delegate's read-only Match
 * Debrief screen prints the same matchday label and cannot import a UM page to get it.
 */
export { tokyoDateKicker };

function FixtureRow({ fixture }: { fixture: FixtureRowView }): React.JSX.Element {
  return (
    <li className="apptline">
      {/*
       * THE WHOLE ROW IS THE LINK — not only the match-number chip. The chip alone is a
       * ~58px target, and on a phone a thumb lands on the competition name or the time and
       * nothing happens; the TD hit exactly this in live testing (2026-09-09) and read it as
       * navigation being broken, because from a phone that is what it is. The grid layout
       * moves onto the anchor (`apptline__link`), the list item keeps the hairline.
       */}
      <Link
        className="apptline__link"
        to={`${UM_ROOT_PATH}/match/${String(fixture.tmsMatchDbId)}?competition=${fixture.competitionId}`}
      >
        {/* Colour is never the only channel (gap A4): the letter-prefixed fixture id carries
            the competition as text, and the class carries it as colour. */}
        <span
          className={`apptline__id apptline__id--${fixture.competitionId === "1916" ? "men" : "women"}`}
        >
          {fixture.displayMatchNo}
        </span>
        <span className="apptline__what">
          {fixture.competitionId === "1916" ? "Men's Competition" : "Women's Competition"}
          {fixture.pitch === null ? "" : ` · ${fixture.pitch}`}
        </span>
        <span className="apptline__when num">{fixture.time}</span>
      </Link>
    </li>
  );
}

/**
 * One row of the two debrief blocks below: a finished match this manager captured against.
 *
 * `unreleasedResponses` is the count of HER OWN debrief responses that have not been released —
 * spec 01 §2.7's constraint, unchanged by ruling R4″: the number of a manager's unreleased notes
 * is computed on her own device, from her own `GET /notes/mine`, for her own eyes. No
 * TD-reachable surface produces it and this one does not send it anywhere.
 */
interface DebriefRow {
  tmsMatchDbId: number;
  competitionId: string;
  displayMatchNo: string;
  date: string;
  unreleasedResponses: number;
}

/**
 * The finished matches this manager has captured against, newest first.
 *
 * **Derived from her own notes and the schedule, and from nothing else.** A match she has
 * written nothing about is not her debrief to hold: spec 13 §1 puts *"the Umpires Managers who
 * covered the match"* in the room, and having captured against it is the only evidence this
 * system holds of who that was.
 */
export function debriefRows(
  notes: readonly OwnNoteView[],
  fixtures: readonly FixtureRowView[],
  now: Date = new Date(),
): DebriefRow[] {
  const byMatch = new Map<number, DebriefRow>();
  for (const note of notes) {
    if (note.matchId === null) continue;
    const fixture = fixtures.find((row) => row.tmsMatchDbId === note.matchId);
    if (fixture === undefined || !matchHasLikelyEnded(fixture, now)) continue;
    const row = byMatch.get(note.matchId) ?? {
      tmsMatchDbId: fixture.tmsMatchDbId,
      competitionId: fixture.competitionId,
      displayMatchNo: fixture.displayMatchNo,
      date: fixture.date,
      unreleasedResponses: 0,
    };
    if (note.kind === "debrief_response" && note.visibility !== "released") {
      row.unreleasedResponses += 1;
    }
    byMatch.set(note.matchId, row);
  }
  return [...byMatch.values()].sort((left, right) => right.date.localeCompare(left.date));
}

/** One match on the release reminder: where the drafts are, and how many of them. */
export interface ReleaseReminderMatch {
  tmsMatchDbId: number;
  competitionId: string;
  displayMatchNo: string;
  time: string;
  drafts: number;
}

/** The release reminder, when one is due. */
export interface ReleaseReminder {
  /** Every draft the card counts, across the day's matches. */
  drafts: number;
  /** The matches holding them, in kick-off order. */
  matches: ReleaseReminderMatch[];
  /** `HH:MM` Tokyo — when tonight's Daily Report builds, so the card can say so. */
  buildsAt: string;
}

/** The part of `GET /um/debrief/schedule` the reminder is timed by. */
export type ReportTimes = Pick<
  DailyReportTimesBody,
  "day" | "matchday" | "fullTimeMinutes" | "buildMinutes" | "buildsAt"
>;

/** Minutes past midnight on the Tokyo clock — the unit the report's two moments come in. */
export function tokyoMinutesOfDay(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const read = (type: "hour" | "minute"): number =>
    Number.parseInt(parts.find((part) => part.type === type)?.value ?? "0", 10);
  return read("hour") * 60 + read("minute");
}

/**
 * RUN 79c (QA pass 3) — which report times the reminder stands on, as a function of its own so the
 * failure branch is pinned directly: the read's own answer whenever it has one, and otherwise —
 * while a re-read is out, or after it failed — the last good answer, but only for the same day.
 */
export function reportTimesFor(
  read: AsyncState<DailyReportTimesBody>,
  lastGood: DailyReportTimesBody | null,
  today: string,
): DailyReportTimesBody | null {
  if (read.status === "ready") return read.data;
  return lastGood !== null && lastGood.day === today ? lastGood : null;
}

/**
 * RUN 79 — spec 11 §4.1's release reminder, as the Technical Delegate ruled it on 2026-09-13;
 * **bounded by run 79a** after the QA gate.
 *
 * **When it is due.** From the full time of the day's LAST match UNTIL the Daily Report builds,
 * and only on a matchday. After the build, a note released that night is not in the report, so a
 * card still asking for it would be asking for the wrong thing; a late kick-off that the backstop
 * builds in front of leaves no window, and the card honestly never appears. Both moments come
 * from `GET /um/debrief/schedule` (`times`): the nightly build's own arithmetic over the fixtures
 * and the Technical Delegate's configured delay and backstop. **Without them there is no card** —
 * a reminder that guessed the deadline could send a manager to release after it.
 *
 * **What it counts: the reader's own DRAFTS from today's matches, and nothing else**, from her
 * own `GET /notes/mine`, on her own device (spec 01 §2.7 — no TD-reachable surface produces the
 * number, and the server is never asked who holds unreleased notes). A **locked** note is a
 * decision to keep it private, and a reminder must never press anybody to undo that decision; a
 * released note needs nothing. **Debrief responses** are left to their own per-match nudge above,
 * so no note is ever counted on two cards.
 */
export function releaseReminder(
  notes: readonly OwnNoteView[],
  fixtures: readonly FixtureRowView[],
  times: ReportTimes | null,
  now: Date = new Date(),
): ReleaseReminder | null {
  const today = tokyoToday(now);
  if (times === null || !times.matchday || times.day !== today) return null;
  const { fullTimeMinutes, buildMinutes, buildsAt } = times;
  if (fullTimeMinutes === null || buildMinutes === null || buildsAt === null) return null;
  const minute = tokyoMinutesOfDay(now);
  if (minute < fullTimeMinutes || minute >= buildMinutes) return null;

  const todays = fixtures.filter((fixture) => fixture.date === today);
  if (todays.length === 0) return null;

  const byMatch = new Map<number, ReleaseReminderMatch>();
  for (const note of notes) {
    if (note.visibility !== "draft" || note.kind === "debrief_response") continue;
    if (note.matchId === null) continue;
    const fixture = todays.find((row) => row.tmsMatchDbId === note.matchId);
    if (fixture === undefined) continue;
    const row = byMatch.get(fixture.tmsMatchDbId) ?? {
      tmsMatchDbId: fixture.tmsMatchDbId,
      competitionId: fixture.competitionId,
      displayMatchNo: fixture.displayMatchNo,
      time: fixture.time,
      drafts: 0,
    };
    row.drafts += 1;
    byMatch.set(fixture.tmsMatchDbId, row);
  }
  if (byMatch.size === 0) return null;

  const matches = [...byMatch.values()].sort(
    (left, right) =>
      left.time.localeCompare(right.time) || left.tmsMatchDbId - right.tmsMatchDbId,
  );
  return { drafts: matches.reduce((sum, row) => sum + row.drafts, 0), matches, buildsAt };
}

/**
 * RUN 79b — how often Today re-reads the report's two times. The scheduler reads the Technical
 * Delegate's settings on every tick; five minutes is the most a changed setting can be stale by on
 * an open screen.
 */
const TIMES_REREAD_MS = 5 * 60_000;

export function UmTodayPage(): React.JSX.Element {
  /*
   * RUN 79 — the screen's own clock. The release reminder is due at a MOMENT, the full time of
   * the day's last match, and a manager who opened Today before it must see the card arrive
   * rather than having to leave the screen and come back. Once a minute is ample for a card
   * whose moment is an approximation measured in minutes.
   */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const today = tokyoToday(now);

  // The schedule is read to answer one question: which day's matches should this screen
  // offer? Today's, when today has any; otherwise the next day that does — an empty screen
  // on a rest day would leave a UM with no way to reach a match at all. It is a fact about
  // fixtures and never about anybody's notes.
  const schedule = useAsync(() => fetchFixtureDays(), []);

  const days =
    schedule.status === "ready"
      ? [...new Set(schedule.data.fixtures.map((fixture) => fixture.date))].sort()
      : [];
  const day = days.includes(today) ? today : (days.find((date) => date > today) ?? null);

  const fixtures = useAsync(
    () => (day === null ? Promise.resolve(null) : fetchFixturesForDay(day)),
    [day],
  );

  /**
   * The manager's own notes, tournament-wide, read for the debrief blocks below.
   *
   * A failure costs the two blocks and nothing else — the fixtures list and every route into
   * capture stand — which is why it is a separate read rather than a field on anything.
   */
  const notes = useAsync(() => fetchMyNotes(), []);
  const debriefs = debriefRows(
    notes.status === "ready" ? notes.data.notes : [],
    schedule.status === "ready" ? schedule.data.fixtures : [],
    now,
  );
  const nudges = debriefs.filter((row) => row.unreleasedResponses > 0);
  /**
   * RUN 79a — the day's full time and Daily Report build time, for the reminder's window.
   *
   * RUN 79b (QA re-review) — **re-read every five minutes**, not once a day. The scheduler reads
   * the Technical Delegate's delay and backstop on every tick, so a setting changed during the
   * evening moves the build, and a screen that read the times once would go on promising the old
   * one. The same re-read retries a read that failed. While a re-read is out, or if it fails, the
   * last good answer for the same day stands, so the card does not blink out between reads.
   */
  const timesBucket = Math.floor(now.getTime() / TIMES_REREAD_MS);
  const reportTimes = useAsync(() => fetchDailyReportTimes(today), [today, timesBucket]);
  const [lastTimes, setLastTimes] = useState<DailyReportTimesBody | null>(null);
  useEffect(() => {
    if (reportTimes.status === "ready") setLastTimes(reportTimes.data);
  }, [reportTimes]);
  const times = reportTimesFor(reportTimes, lastTimes, today);
  const reminder = releaseReminder(
    notes.status === "ready" ? notes.data.notes : [],
    schedule.status === "ready" ? schedule.data.fixtures : [],
    times,
    now,
  );

  return (
    <>
      <header className="umsect">
        <span className="umsect__kicker">{tokyoDateKicker(today)}</span>
        <h2>Today</h2>
        <HowToUse id="um-today" title="How to use Today">
          <li>
            <HowToLink to="day-fixtures">The match list</HowToLink> on this screen is the day&rsquo;s
            fixtures. Tap a match number to open it.
          </li>
          <li>
            You can capture on any match, whether or not it is yours. One short note at a time,
            while you still remember it.
          </li>
          <li>
            After the day&rsquo;s last match and until the Daily Report builds, a{" "}
            <b>Release Before The Daily Report Builds</b> card appears here if any of today&rsquo;s
            notes are still drafts, with the time the report builds. Tap a match on it to open the
            match and release them. Locked notes stay private and are never counted.
          </li>
          {/*
           * RULING R4″ (Alfred, the Technical Delegate, 2026-09-09) — the disclosure changed in
           * the same run as the capability. R4′ promised the TD could see only THAT you had
           * unreleased notes; he can now see how many and when. The words a manager reads must
           * say so, because an undisclosed capability is the failure mode.
           */}
          <li>
            The words you write stay private to you until you release them. The Technical
            Delegate can see how many notes you have recorded, how many are pending and how many
            released, and when you last recorded &mdash; never what your drafts and locked notes
            say.
          </li>
          <li>
            <b>Umpires</b> at the foot of the screen is where your own umpires&rsquo; marks and
            reports live.
          </li>
        </HowToUse>
      </header>

      {schedule.status === "loading" || fixtures.status === "loading" ? (
        <Skeleton rows={4} />
      ) : null}
      {schedule.status === "error" ? <FailBanner error={schedule.error} /> : null}
      {fixtures.status === "error" ? <FailBanner error={fixtures.error} /> : null}

      {schedule.status === "ready" ? (
        <section className="card">
          <div className="card__head">
            <span className="card__kicker">No Match Appointed To You Today</span>
            <span className="card__note num">{today}</span>
          </div>
          <p className="card__body">
            You Can Still Capture On Any Match Below. Umpire appointments are not held in
            this system yet, so no match is shown as yours and no umpire tiles are drawn.
          </p>
        </section>
      ) : null}

      {/*
       * SPEC 01 §2.7's nudge, in the debrief's own words — `M07 Debrief — 4 Responses Not Yet
       * Released`, warn-toned, with the single control that routes to the screen that fixes it.
       *
       * §2.7 puts it **above** the `Also On Your Day` block, and that is where it is: it is the
       * one thing on this screen that is about something already done and not yet finished.
       *
       * **The count is the reader's own, computed on the reader's own device.** No TD-reachable
       * screen, query, export or log produces it (§2.7, ruling R4′ as R4″ left it).
       */}
      {nudges.map((row) => (
        <section className="card card--nudge" key={row.tmsMatchDbId} data-testid={`debrief-nudge-${row.tmsMatchDbId}`}>
          <div className="card__head">
            <span className="card__kicker">
              {debriefNudgeLine(row.displayMatchNo, row.unreleasedResponses)}
            </span>
          </div>
          <p className="card__body">
            They are still yours alone. Nobody on the panel can read them and neither can the
            Technical Delegate.
          </p>
          <Link
            className="gbtn gbtn--solid"
            to={matchDebriefPath(row.tmsMatchDbId, row.competitionId, "today")}
          >
            Open Match Debrief
          </Link>
        </section>
      ))}

      {/*
       * RUN 79 — spec 11 §4.1's release reminder: a card, and only a card (the Technical
       * Delegate's ruling of 2026-09-13). `releaseReminder` says when it is due and what it
       * counts. Each row is the way into that match's capture screen, where `Release` lives —
       * the same whole-row link idiom as the fixture list, so a thumb that lands anywhere on the
       * row gets there.
       */}
      {reminder === null ? null : (
        <section className="card card--nudge" data-testid="release-reminder">
          <div className="card__head">
            <span className="card__kicker">Release Before The Daily Report Builds</span>
          </div>
          <p className="card__body">
            You have {reminder.drafts} {reminder.drafts === 1 ? "draft" : "drafts"} from today that
            the panel hasn&rsquo;t seen. The Daily Report builds at{" "}
            <span className="num">{reminder.buildsAt}</span> and reads released notes only.
          </p>
          <ul className="apptlines">
            {reminder.matches.map((row) => (
              <li className="apptline" key={row.tmsMatchDbId}>
                <Link
                  className="apptline__link"
                  data-testid={`release-reminder-${String(row.tmsMatchDbId)}`}
                  to={`${UM_ROOT_PATH}/match/${String(row.tmsMatchDbId)}?competition=${row.competitionId}`}
                >
                  <span
                    className={`apptline__id apptline__id--${row.competitionId === "1916" ? "men" : "women"}`}
                  >
                    {row.displayMatchNo}
                  </span>
                  <span className="apptline__what">
                    {row.drafts} {row.drafts === 1 ? "Draft" : "Drafts"} To Release
                  </span>
                  <span className="apptline__when num">{row.time}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
       * FINDING 1(b) — the way into a finished match's debrief, on the screen a manager opens
       * first. It is the `.apptline` row idiom this screen already uses for a fixture, so a
       * debrief is reached exactly the way a match is, and no new component was drawn for it.
       */}
      {debriefs.length === 0 ? null : (
        <section className="card" data-testid="debrief-matches">
          <div className="card__head">
            <span className="card__kicker">Matches You Captured On</span>
            <span className="card__note">Match Debrief</span>
          </div>
          <ul className="apptlines">
            {debriefs.map((row) => (
              <li className="apptline" key={row.tmsMatchDbId}>
                <Link
                  className="apptline__link"
                  to={matchDebriefPath(row.tmsMatchDbId, row.competitionId, "today")}
                >
                  <span
                    className={`apptline__id apptline__id--${row.competitionId === "1916" ? "men" : "women"}`}
                  >
                    {row.displayMatchNo}
                  </span>
                  <span className="apptline__what">Match Debrief</span>
                  <span className="apptline__when num">{tokyoDateKicker(row.date)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {fixtures.status === "ready" && fixtures.data !== null && day !== null ? (
        <section className="card">
          <div className="card__head">
            <span className="card__kicker">
              {day === today ? "Also On Your Day" : "The Next Match Day"}
            </span>
            <span className="card__note num">{tokyoDateKicker(day)}</span>
          </div>
          {fixtures.data.fixtures.length === 0 ? (
            <p className="card__body">There are no fixtures on {tokyoDateKicker(day)}.</p>
          ) : (
            <ul className="apptlines" data-howto="day-fixtures">
              {fixtures.data.fixtures.map((fixture) => (
                <FixtureRow key={fixture.tmsMatchDbId} fixture={fixture} />
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {schedule.status === "ready" && day === null ? (
        <section className="card">
          <p className="card__body">
            The tournament schedule carries no matches from today onwards.
          </p>
        </section>
      ) : null}
    </>
  );
}
